'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { StorefrontExtension } from './types';

type HeaderChrome = StorefrontExtension['headerChrome'];

const HeaderChromeContext = createContext<HeaderChrome>(undefined);

/**
 * Carries this tenant's vertical header chrome from the server down to the shell block.
 *
 * <p>The chrome is chosen on the server, from the site descriptor (`headerChromeFor`), but
 * it is used by `SiteShellBlock`, which the block resolver renders deep inside the client
 * tree — there is no prop path between the two. A context is the path.
 *
 * <p>What crosses the boundary is a REFERENCE to a client component, never a call: the
 * server names it, the browser renders it. That is the same shape `catalogueSurface` and
 * `tradeSkinOverrides` already rely on, and the reason each piece of chrome carries
 * `'use client'`.
 *
 * <p>The default is `undefined`, which is a real answer: a shop whose vertical ships no
 * chrome, and any tree mounted without this provider, both get the shell's own header
 * unchanged.
 */
export function HeaderChromeProvider({
  value,
  children,
}: {
  value: HeaderChrome;
  children: ReactNode;
}) {
  return <HeaderChromeContext.Provider value={value}>{children}</HeaderChromeContext.Provider>;
}

export function useHeaderChrome(): HeaderChrome {
  return useContext(HeaderChromeContext);
}
